import { z } from "zod";
import { petSchema, petProfilePublicInfoSchema } from "./pet";
import { userLocationSchema } from "./location";
import { createdAtSchema, createdBySchema, updatedAtSchema, updatedBySchema, uuidSchema } from "./common";
import { addressSchema } from "./location";
import { EUserTypeV4 } from "../constants";

/**
 * Interface for API pagination metadata
 */
export interface PaginationMeta {
  totalCount: number;
  pageSize: number;
  limit: number;
  currentPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Interface for paginated API response
 */
export interface PaginatedResponse<T> {
  items: T[];
  meta: PaginationMeta;
}


/**
 * User data schema with complete validation
 */
export const userDataSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(100).optional(), // Some users may not have a name
  phone: z.string().regex(/^\+?[0-9]{10,15}$/, "Phone number must be between 10 and 15 digits").optional(),
  phoneNumber: z.string().regex(/^\+?[0-9]{10,15}$/, "Phone number must be between 10 and 15 digits").optional(), // For backward compatibility
  email: z.string().email("Must be a valid email address").optional(), // Some users may not have an email
  photoUrl: z.string().url("Must be a valid URL").optional().nullable(),
  profilePictureUrl: z.string().url("Must be a valid URL").optional().nullable(), // New API uses this field
  bio: z.string().optional(),
  location: userLocationSchema.optional(),
  pets: z.array(petSchema).optional(),
  petProfiles: z.array(petSchema).optional(), // For the new API format
  isOnboarded: z.boolean().optional(),
  juspayCustomerId: z.string().optional(),
  shopifyCustomerId: z.string().optional(),
  freshDeskId: z.string().optional(),
  isExistingUser: z.boolean().optional(),
  createdAt: z.union([z.string(), z.number()]).optional(),
  updatedAt: z.union([z.string(), z.number()]).optional(),
  addresses: z.array(addressSchema).optional(),
  type: z.string().optional(), // User type from API
});

export type UserData = z.infer<typeof userDataSchema>;

/**
 * User basic public info schema
 * Used in session assignment request responses and other public-facing APIs
 */
export const userBasicPublicInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    profilePicUrl: z.string().url().nullable(),
    petProfiles: z.array(petProfilePublicInfoSchema),
});

export type IUserBasicPublicInfo = z.infer<typeof userBasicPublicInfoSchema>;

/**
 * Basic customer details schema
 * Used in session assignment request detail responses
 */
export const basicCustomerDetailsSchema = z.object({
    id: z.string(),
    name: z.string(),
    photoUrl: z.string().url().nullable(),
});

export type IBasicCustomerDetails = z.infer<typeof basicCustomerDetailsSchema>;

/**
 * V4 Schema for user
 */

export const auditUserResponseDtoSchema = z.object({
  id: uuidSchema().describe("Unique identifier for the user"),
  name: z.string().describe("Name of the user"),
  email: z.string().email().nullish().describe("Email of the user"),
  type: z.nativeEnum(EUserTypeV4).describe("Type of the user"),
  role: z.string().nullish().describe("Role of the user"),
  createdAt: createdAtSchema.describe("Time when the user was created in epoch milliseconds"),
  updatedAt: updatedAtSchema.describe("Time when the user was last updated in epoch milliseconds"),
  createdBy: createdBySchema.describe("User ID who created the user"),
  updatedBy: updatedBySchema.describe("User ID who last updated the user"),
});

export type IAuditUserResponseDto = z.infer<typeof auditUserResponseDtoSchema>;